import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Modal } from '@components/index';
import { PetProfileUpdateProvider, usePetProfileUpdateContext } from './context';
import { PetInfoStep, RecipeCustomizationStep, RecipeUpdateDiff } from './steps';
import { PetHealthStep } from './steps/PetHealthStep';
import { PetHealthPreviewStep } from './steps/PetHealthPreviewStep';
import { PuppyToAdultWeightStep } from '../pet-transition/PuppyToAdultWeightStep';
import { UpdateMode } from './types';
import { ErrorIcon } from './icons';
import {
  getPetSelector,
  getSubscriptionRecipesSelector,
  getRecalculatedProductSelector,
  getIsUpdatingPetProfileSelector,
} from '../../core/selectors';

const CloseIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const PetProfileUpdateContent: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const intl = useIntl();
  const ctx = usePetProfileUpdateContext();
  const { step, mode, error } = ctx;

  const pet = useSelector(getPetSelector);
  const currentRecipes = useSelector(getSubscriptionRecipesSelector);
  const recalculatedProduct = useSelector(getRecalculatedProductSelector);
  const isUpdating = useSelector(getIsUpdatingPetProfileSelector);

  return (
    <div className="pet-profile-update-modal">
      <button
        type="button"
        className="pet-profile-update-modal__close"
        onClick={onClose}
        aria-label={intl.formatMessage({ id: 'GLOBAL.CLOSE' })}>
        <CloseIcon />
      </button>
      {error && (
        <div className="ppu-alert ppu-alert--error mb-6">
          <div className="ppu-alert__icon">
            <ErrorIcon />
          </div>
          <div className="ppu-alert__content">
            <p className="ppu-alert__title">Ein Fehler ist aufgetreten</p>
            <p className="ppu-alert__text">{error}</p>
          </div>
        </div>
      )}
      <div className="pet-profile-update-modal__content">
        {mode === 'recalculation' ? (
          // Recalculation flow: read-only before/after diff, no input/selection.
          <RecipeUpdateDiff />
        ) : (
          <>
            {step === 'pet-info' && (
              <>
                {mode === 'weight' && <PetInfoStep />}
                {mode === 'health' && <PetHealthStep />}
                {mode === 'transition' && <PuppyToAdultWeightStep />}
              </>
            )}
            {step === 'customize' && (
              <>
                {mode === 'health' && pet ? (
                  <PetHealthPreviewStep
                    pet={pet as any}
                    selectedAllergies={ctx.healthInfo.allergies}
                    selectedHealthIssues={ctx.healthInfo.healthIssues}
                    recalculatedProduct={recalculatedProduct}
                    currentRecipes={currentRecipes}
                    onBack={ctx.goBack}
                    onSave={(variantId) => {
                      // Override selectedVariant with the health preview's variant
                      if (variantId) {
                        ctx.handleSubmit();
                      } else {
                        ctx.handleSubmit();
                      }
                    }}
                    isUpdating={isUpdating}
                  />
                ) : (
                  <RecipeCustomizationStep />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export interface PetProfileUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: UpdateMode;
}

export const PetProfileUpdateModal: React.FC<PetProfileUpdateModalProps> = ({
  isOpen,
  onClose,
  mode = 'weight',
}) => {
  const pet = useSelector(getPetSelector);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} handleClose={onClose}>
      <div className="max-w-[120rem] mx-auto">
        <PetProfileUpdateProvider
          onClose={onClose}
          mode={mode}
          dogStageOverride={mode === 'transition' ? 'adult' : undefined}
          initialWeightOverride={mode === 'transition' ? pet?.adult_weight : undefined}>
          <PetProfileUpdateContent onClose={onClose} />
        </PetProfileUpdateProvider>
      </div>
    </Modal>
  );
};
